import type { ApiEnvelope } from '../types'
import { api } from './api'

export interface CompaniesHouseAuthorizeResponse {
  success: boolean
  data: {
    authUrl: string
    state: string
    companyNumber: string
    scopes: string[]
    expiresAt: string
  }
  message?: string
}

export interface CompaniesHouseTokenStatus {
  hasValidToken: boolean
  tokenType?: string
  scope?: string
  expiresAt?: string
  issuedAt?: string
}

export interface CompaniesHouseTransaction {
  id: string
  company_number: string
  description: string
  reference?: string
  status: 'open' | 'closed'
  resources: Record<string, unknown>
  links: Record<string, string>
  created_at: string
  closed_at?: string
}

export interface RegisteredOfficeAddress {
  premises?: string
  address_line_1: string
  address_line_2?: string
  locality: string
  region?: string
  postal_code: string
  country?: string
  po_box?: string
  accept_appropriate_office_address_statement: boolean
}

export interface RegisteredEmailAddress {
  registered_email_address: string
  accept_appropriate_email_address_statement: boolean
}

/**
 * pending          — received by Companies House, awaiting their decision
 * parked           — CH is waiting for more information
 * accepted         — on the register
 * rejected         — refused on receipt or by CH processing (see `rejections`)
 * internal_failure — CH-side failure; contact CH if it persists
 * failed           — CH never received it; safe to submit again
 */
export type ConfirmationStatementFilingStatus =
  | 'pending'
  | 'parked'
  | 'accepted'
  | 'rejected'
  | 'internal_failure'
  | 'failed'

export interface ConfirmationStatementFiling {
  id: string
  companyNumber: string
  companyName: string | null
  /** Confirmation date the statement is made up to (YYYY-MM-DD). */
  reviewDate: string
  formType: 'ConfirmationStatement' | 'ConfirmationAndVerificationStatement'
  verifiedDirectors: string[]
  submissionNumber: string
  status: ConfirmationStatementFilingStatus
  statusMessage: string | null
  rejections: Array<{ code: string; message: string }>
  examinerComment: string | null
  testMode: boolean
  submittedAt: string
  receivedAt: string | null
  lastPolledAt: string | null
  completedAt: string | null
}

/** Identity-verification details for a director who hasn't verified with Companies House yet. */
export interface DirectorVerificationInput {
  /** Name exactly as on the register, e.g. "ADETUTU, Toluwani". */
  name: string
  /** 11-character Companies House personal code. */
  code: string
  /** Full date of birth, YYYY-MM-DD. */
  dateOfBirth?: string
}

export interface ConfirmationStatementSubmitResult {
  success?: boolean
  status?: ConfirmationStatementFilingStatus
  submissionNumber?: string
  errors?: Array<{ code: string; message: string }>
  message?: string
  filing?: ConfirmationStatementFiling
}

class CompaniesHouseAuthService {
  /**
   * Initialize Companies House OAuth authorization
   * @param companyNumber - The company number (8 alphanumeric characters)
   * @returns Authorization URL and state
   */
  async authorize(
    companyNumber: string
  ): Promise<ApiEnvelope<CompaniesHouseAuthorizeResponse>> {
    return api.get<CompaniesHouseAuthorizeResponse>(
      `/companies-house/filing/auth/url?companyNumber=${encodeURIComponent(companyNumber)}`
    )
  }

  /**
   * Get current Companies House token status
   * @returns Token status information
   */
  async getTokenStatus(companyNumber?: string): Promise<ApiEnvelope<CompaniesHouseTokenStatus>> {
    const query = companyNumber ? `?companyNumber=${encodeURIComponent(companyNumber)}` : ''
    return api.get<CompaniesHouseTokenStatus>(
      `/companies-house/filing/auth/token-status${query}`
    )
  }

  /**
   * Check if the user has a valid Companies House token. Pass the company number
   * to also verify the token's scope covers that company — CH tokens are
   * company-scoped, so one company's authorisation doesn't file for another.
   * @returns true if valid token exists
   */
  async hasValidToken(companyNumber?: string): Promise<boolean> {
    try {
      const envelope = await this.getTokenStatus(companyNumber)
      const payload = (envelope.response ?? {}) as any
      return (payload.hasValidToken ?? payload.data?.hasValidToken) ?? false
    } catch (error) {
      console.error('Error checking Companies House token status:', error)
      return false
    }
  }
}

class CompaniesHouseFilingService {
  /**
   * Create a new transaction
   * @param companyNumber - Company number (8 alphanumeric characters)
   * @param description - Transaction description
   * @param reference - Optional internal reference
   * @returns Transaction details
   */
  async createTransaction(
    companyNumber: string,
    description: string,
    reference?: string
  ): Promise<ApiEnvelope<CompaniesHouseTransaction>> {
    return api.post<CompaniesHouseTransaction>(
      '/companies-house/filing/transactions',
      {
        company_number: companyNumber,
        description,
        reference,
      }
    )
  }

  /**
   * Submit registered office address change
   * @param transactionId - Transaction ID from createTransaction
   * @param address - New registered office address
   * @returns Transaction result with filed address
   */
  async submitRegisteredOfficeAddress(
    transactionId: string,
    address: RegisteredOfficeAddress
  ): Promise<ApiEnvelope<CompaniesHouseTransaction>> {
    return api.post<CompaniesHouseTransaction>(
      `/companies-house/filing/transactions/${transactionId}/registered-office-address`,
      address
    )
  }

  /**
   * Submit registered email address change
   * @param transactionId - Transaction ID from createTransaction
   * @param emailData - New registered email address
   * @returns Transaction result with filed email
   */
  async submitRegisteredEmailAddress(
    transactionId: string,
    emailData: RegisteredEmailAddress
  ): Promise<ApiEnvelope<CompaniesHouseTransaction>> {
    return api.post<CompaniesHouseTransaction>(
      `/companies-house/filing/transactions/${transactionId}/registered-email-address`,
      emailData
    )
  }

  /**
   * File a CS01 Confirmation Statement through the Companies House XML
   * gateway. A `pending` result means Companies House has RECEIVED it — they
   * accept or reject later, which the backend picks up automatically.
   *
   * Errors arrive as ApiError: 402 `payment_required`, 409 `already_submitted`,
   * 400 `director_verification_required` / `invalid_statement_date`.
   */
  async submitConfirmationStatement(payload: {
    companyNumber: string
    companyName?: string
    statementDate?: string
    companyAuthCode?: string
    personalCodes?: DirectorVerificationInput[]
  }): Promise<ApiEnvelope<ConfirmationStatementSubmitResult>> {
    return api.post<ConfirmationStatementSubmitResult>('/companies-house/filing/confirmation-statement', payload)
  }

  /** The signed-in user's Confirmation Statement filings, newest first. */
  async listConfirmationStatementFilings(
    companyNumber?: string
  ): Promise<ApiEnvelope<{ success?: boolean; data?: ConfirmationStatementFiling[] }>> {
    const query = companyNumber ? `?companyNumber=${encodeURIComponent(companyNumber)}` : ''
    return api.get(`/companies-house/filing/confirmation-statements${query}`)
  }

  /**
   * Which filing channel the backend will use, and whether the Companies House
   * fee is included in our price. When `feeIncluded` is true we settle the fee
   * from our Companies House credit account, so the user pays once and is not
   * redirected to GOV.UK Pay.
   */
  async getConfirmationStatementCapability(): Promise<
    ApiEnvelope<{
      data?: {
        channel?: 'xml-gateway' | 'rest-oauth'
        feeIncluded?: boolean
        requiresCompanyAuthCode?: boolean
        redirectsToGovUkPay?: boolean
      }
    }>
  > {
    return api.get('/companies-house/filing/confirmation-statement/capability')
  }

  /**
   * Latest outcome of one of the user's gateway CS01 submissions. The backend
   * asks Companies House afresh while it is still outstanding.
   */
  async getConfirmationStatementSubmissionStatus(
    submissionNumber: string
  ): Promise<ApiEnvelope<ConfirmationStatementSubmitResult>> {
    return api.get(`/companies-house/filing/confirmation-statement/${encodeURIComponent(submissionNumber)}/status`)
  }

  /**
   * Access probe — check a company is eligible to file (needs Companies House
   * OAuth authorisation). Targets sandbox unless the backend CH_CS_REST_LIVE is set.
   */
  async checkConfirmationStatementEligibility(companyNumber: string): Promise<ApiEnvelope<any>> {
    return api.get<any>(`/companies-house/filing/confirmation-statement/eligibility/${companyNumber}`)
  }

  /**
   * File a confirmation statement via the transactions REST API (SDK-based).
   * Returns a status: 'submitted' (free filing done), 'payment_required' (with a
   * paymentUrl to redirect to GOV.UK Pay), 'authorization_required', 'ineligible',
   * or 'validation_failed'.
   */
  async fileConfirmationStatementRest(payload: {
    companyNumber: string
    madeUpToDate?: string
  }): Promise<
    ApiEnvelope<{
      status?: 'submitted' | 'payment_required' | 'authorization_required' | 'ineligible' | 'validation_failed'
      transactionId?: string
      confirmationStatementId?: string
      paymentUrl?: string | null
      message?: string
      mode?: 'live' | 'sandbox'
    }>
  > {
    return api.post('/companies-house/filing/confirmation-statement/file', payload)
  }

  /**
   * Get transaction status
   * @param transactionId - Transaction ID
   * @returns Transaction details
   */
  async getTransactionStatus(
    transactionId: string
  ): Promise<ApiEnvelope<CompaniesHouseTransaction>> {
    return api.get<CompaniesHouseTransaction>(
      `/companies-house/filing/transactions/${transactionId}`
    )
  }

  /**
   * Submit a complete registered office address change
   * This is a convenience method that creates a transaction and submits the address in one call
   * @param companyNumber - Company number
   * @param address - New registered office address
   * @param reference - Optional internal reference
   * @returns Final transaction result
   */
  async changeRegisteredOfficeAddress(
    companyNumber: string,
    address: RegisteredOfficeAddress,
    reference?: string
  ): Promise<{ success: boolean; transaction: CompaniesHouseTransaction; message?: string }> {
    try {
      // Step 1: Create transaction
      const txResponse = await this.createTransaction(
        companyNumber,
        'Change of registered office address',
        reference || `ADDR-${Date.now()}`
      )

      if (!txResponse.response?.id) {
        throw new Error('Failed to create transaction')
      }

      const transactionId = txResponse.response.id

      // Step 2: Submit address change
      const filingResponse = await this.submitRegisteredOfficeAddress(
        transactionId,
        address
      )

      return {
        success: true,
        transaction: filingResponse.response,
        message: 'Registered office address filed successfully',
      }
    } catch (error) {
      console.error('Error filing registered office address:', error)
      throw error
    }
  }
}

export const companiesHouseAuthService = new CompaniesHouseAuthService()
export const companiesHouseFilingService = new CompaniesHouseFilingService()
